import { create } from 'zustand';
import { authAPI } from '../api/client';

// Inactivity timeout: 15 minutes (standard for financial applications)
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
let _inactivityTimer = null;
let _warningTimer = null;

const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  showInactivityWarning: false,

  setUser: (user) => set({ user, isAuthenticated: !!user }),

  login: async (credentials) => {
    const response = await authAPI.login(credentials);
    const { access_token, refresh_token, user } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('last_activity', Date.now().toString());
    set({ user, isAuthenticated: true });
    get()._startInactivityTimer();
    return user;
  },

  register: async (data) => {
    const response = await authAPI.register(data);
    const { access_token, refresh_token, user } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('last_activity', Date.now().toString());
    set({ user, isAuthenticated: true });
    get()._startInactivityTimer();
    return user;
  },

  socialLogin: async (provider, token, role = 'tenant') => {
    const response = await authAPI.socialLogin({ provider, token, role });
    const { access_token, refresh_token, user } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('last_activity', Date.now().toString());
    set({ user, isAuthenticated: true });
    get()._startInactivityTimer();
    return user;
  },

  logout: (reason) => {
    get()._clearInactivityTimer();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('last_activity');
    set({ user: null, isAuthenticated: false, showInactivityWarning: false });
    if (reason === 'inactivity') {
      sessionStorage.setItem('logout_reason', 'inactivity');
    }
  },

  dismissInactivityWarning: () => {
    set({ showInactivityWarning: false });
    get().resetInactivityTimer();
  },

  resetInactivityTimer: () => {
    const { isAuthenticated } = get();
    if (!isAuthenticated) return;
    localStorage.setItem('last_activity', Date.now().toString());
    get()._startInactivityTimer();
  },

  _startInactivityTimer: () => {
    if (_inactivityTimer) clearTimeout(_inactivityTimer);
    if (_warningTimer) clearTimeout(_warningTimer);

    // Show warning 2 minutes before logout
    _warningTimer = setTimeout(() => {
      const { isAuthenticated } = get();
      if (isAuthenticated) {
        set({ showInactivityWarning: true });
      }
    }, INACTIVITY_TIMEOUT_MS - 2 * 60 * 1000);

    // Auto-logout after full timeout
    _inactivityTimer = setTimeout(() => {
      const { isAuthenticated, logout } = get();
      if (isAuthenticated) {
        logout('inactivity');
      }
    }, INACTIVITY_TIMEOUT_MS);
  },

  _clearInactivityTimer: () => {
    if (_inactivityTimer) { clearTimeout(_inactivityTimer); _inactivityTimer = null; }
    if (_warningTimer) { clearTimeout(_warningTimer); _warningTimer = null; }
  },

  checkAuth: async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      set({ isLoading: false });
      return;
    }
    // Check if session expired due to inactivity while away
    const lastActivity = parseInt(localStorage.getItem('last_activity') || '0', 10);
    if (lastActivity && Date.now() - lastActivity > INACTIVITY_TIMEOUT_MS) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('last_activity');
      sessionStorage.setItem('logout_reason', 'inactivity');
      set({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }
    try {
      const response = await authAPI.getMe();
      localStorage.setItem('last_activity', Date.now().toString());
      set({ user: response.data, isAuthenticated: true, isLoading: false });
      get()._startInactivityTimer();
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('last_activity');
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },
}));

export default useAuthStore;
